/**
 * StatTile — métrica con jerarquía real.
 *
 * La versión anterior era un icono + label 12px + número 24px, todo
 * monoespaciado: la magnitud no pesaba más que su etiqueta. Aquí el número
 * va en la display del tema y la etiqueta baja a overline, que es lo que
 * hace que un dashboard se lea de un vistazo.
 */

import { type ReactNode } from "react";

type Tone = "accent" | "warn" | "danger" | "success" | "muted";

const TONE_VAR: Record<Tone, string> = {
  accent: "var(--bw-accent)",
  warn: "var(--bw-warn)",
  danger: "var(--bw-danger)",
  success: "var(--bw-success)",
  muted: "var(--bw-muted)",
};

interface StatTileProps {
  label: string;
  value: ReactNode;
  /** Sufijo pequeño pegado al número: "usd", "%", "/7", "días". */
  unit?: string;
  /** Línea inferior: contexto o delta. */
  foot?: string;
  footTone?: Tone;
  icon?: ReactNode;
  tone?: Tone;
  /** Serie para el sparkline de fondo. Se normaliza sola. */
  spark?: number[];
}

function sparkPath(values: number[]): string {
  if (values.length < 2) return "";
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  return values
    .map((v, i) => {
      const x = (i / (values.length - 1)) * 100;
      const y = 30 - ((v - min) / span) * 24;
      return `${i === 0 ? "M" : "L"}${x.toFixed(1)} ${y.toFixed(1)}`;
    })
    .join(" ");
}

export function StatTile({
  label, value, unit, foot, footTone = "muted",
  icon, tone = "accent", spark,
}: StatTileProps) {
  return (
    <div
      className="relative overflow-hidden p-4"
      style={{
        background: "var(--bw-glass-bg)",
        backdropFilter: "blur(var(--bw-glass-blur))",
        WebkitBackdropFilter: "blur(var(--bw-glass-blur))",
        border: "1px solid var(--bw-glass-border)",
        borderRadius: "var(--bw-radius-lg)",
        boxShadow: "var(--bw-elevation-1)",
      }}
    >
      <div className="flex items-center gap-2 mb-2.5">
        {icon && <span style={{ color: TONE_VAR[tone] }}>{icon}</span>}
        <span className="bw-eyebrow text-[var(--bw-muted)]">{label}</span>
      </div>

      <div className="bw-display text-[var(--bw-fg)] text-[30px] md:text-[34px] leading-none">
        {value}
        {unit && (
          <span className="bw-code text-[13px] text-[var(--bw-muted)] ml-1.5 tracking-normal normal-case">
            {unit}
          </span>
        )}
      </div>

      {foot && (
        <div className="bw-code text-[11px] mt-2.5" style={{ color: TONE_VAR[footTone] }}>
          {foot}
        </div>
      )}

      {spark && spark.length > 1 && (
        <svg
          viewBox="0 0 100 34"
          preserveAspectRatio="none"
          aria-hidden="true"
          className="absolute bottom-0 left-0 right-0 h-8 opacity-40 pointer-events-none"
        >
          <path d={sparkPath(spark)} fill="none" stroke={TONE_VAR[tone]} strokeWidth={1.5} />
        </svg>
      )}
    </div>
  );
}
